<template>
  <div class="pipeline-settings">
    <div
      v-for="stage in PIPELINE_TOGGLE_STAGES"
      :key="stage.id"
      class="pipeline-settings__row border rounded p-3 mb-2"
    >
      <div class="form-check mb-0">
        <input
          :id="inputId(stage.id)"
          v-model="localDraft[stage.field]"
          class="form-check-input"
          type="checkbox"
          :disabled="isDisabled(stage)"
          @change="onToggleChange(stage)"
        />
        <label class="form-check-label w-100" :for="inputId(stage.id)">
          <span class="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <span class="fw-semibold">{{ stage.label }}</span>
            <span class="text-muted small text-nowrap">
              <template v-if="stage.stageKey">{{ tokenLabel(stage.stageKey) }}</template>
              <template v-else>0 токенов</template>
            </span>
          </span>
          <span class="d-block text-muted small mt-1">{{ stage.description }}</span>
          <span v-if="stage.perMessage" class="d-block text-muted small">Списание за каждое сообщение в чате</span>
          <span v-if="isDisabled(stage) && stage.requires?.length" class="d-block text-warning small mt-1">
            Сначала включите антириск-скан и дорожную карту
          </span>
        </label>
      </div>
    </div>

    <div class="pipeline-settings__row border rounded p-3 mb-2 bg-light">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <span class="fw-semibold">{{ WINPROB_PIPELINE_META.label }}</span>
        <span class="text-muted small text-nowrap">{{ tokenLabel(WINPROB_PIPELINE_META.stageKey) }}</span>
      </div>
      <span class="d-block text-muted small mt-1">{{ WINPROB_PIPELINE_META.description }}</span>
      <span class="d-block badge bg-secondary mt-2">Всегда включено</span>
    </div>

    <div v-if="showTotals" class="pipeline-settings__totals border-top pt-3 mt-2 small">
      <div class="d-flex justify-content-between">
        <span>Итого за автоанализ</span>
        <strong>{{ formatTokenHint(totals.autoAnalysis) }} токенов</strong>
      </div>
      <div class="d-flex justify-content-between mt-1">
        <span>Дополнительно по запросу</span>
        <strong>{{ formatTokenHint(totals.onDemand) }} токенов</strong>
      </div>
      <p v-if="totals.chatPerMessage != null" class="text-muted mb-0 mt-1">
        Чат: {{ formatTokenHint(totals.chatPerMessage) }} за сообщение (не в сумме доп.)
      </p>
      <div class="d-flex justify-content-between mt-2 pt-2 border-top">
        <span class="fw-semibold">Ориентир на полный цикл</span>
        <strong class="text-primary">{{ formatTokenHint(totals.fullCycle) }} токенов</strong>
      </div>
      <p v-if="hintsFootnote" class="text-muted mb-0 mt-2">{{ hintsFootnote }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import {
  PIPELINE_TOGGLE_STAGES,
  WINPROB_PIPELINE_META,
  PIPELINE_MVP_TOKEN_HINTS,
  computePipelineTokenTotals,
  formatTokenHint,
  isAutoRoadmapAvailable,
  normalizePipelineDraft,
  stageHintTokens,
} from '@/billing/pipelineStages'

const props = defineProps({
  modelValue: { type: Object, required: true },
  hints: { type: Object, default: () => ({}) },
  showTotals: { type: Boolean, default: true },
  idPrefix: { type: String, default: 'pl' },
  /** Подпись под итогами; пустая строка — не показывать. */
  hintsFootnote: {
    type: String,
    default: 'Средние значения по вашим списаниям за 90 дней; фактический расход зависит от объёма документов.',
  },
  /** ЛК: всегда ориентиры MVP, без медианы из API. */
  useMvpHints: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const localDraft = reactive({ ...props.modelValue })

watch(
  () => props.modelValue,
  (v) => {
    Object.assign(localDraft, v || {})
  },
  { deep: true },
)

const effectiveHints = computed(() => {
  if (props.useMvpHints) return PIPELINE_MVP_TOKEN_HINTS
  const stages = props.hints?.stages
  if (stages && Object.keys(stages).length > 0) return props.hints
  return PIPELINE_MVP_TOKEN_HINTS
})

const totals = computed(() => computePipelineTokenTotals(localDraft, effectiveHints.value))

function inputId(stageId) {
  return `${props.idPrefix}-${stageId}`
}

function isDisabled(stage) {
  if (stage.id !== 'auto_roadmap') return false
  return !isAutoRoadmapAvailable(localDraft)
}

function tokenLabel(stageKey) {
  const n = stageHintTokens(effectiveHints.value, stageKey)
  return n != null ? `${formatTokenHint(n)} токенов` : '—'
}

function onToggleChange(stage) {
  if (stage.id === 'compliance' || stage.id === 'roadmap') {
    normalizePipelineDraft(localDraft)
  }
  emit('update:modelValue', { ...localDraft })
}
</script>

<style scoped>
.pipeline-settings__row {
  background: var(--bs-body-bg, #fff);
}
.pipeline-settings__totals strong {
  font-variant-numeric: tabular-nums;
}
</style>
